<template>
    <div class="d-flex">
        <b-input-group
            class="spl-inputs-search w-auto rounded-pill bg-white border overflow-hidden"
            :class="{'filled': value}"
        >
            <b-input-group-prepend is-text>
                <b-icon icon="search"></b-icon>
            </b-input-group-prepend>
            <b-form-input
                v-model="localValue"
                :id="'searchFilterInput-' + id"
                :placeholder="finalPlaceholder"
                :lazy="lazy"
                type="search"
                @keydown.enter="emitSearch"
                @keydown.tab="emitSearch"
            ></b-form-input>
            <!-- need to make it type="search" - and style accordingly -->
            <b-input-group-append>
                <b-button 
                    :disabled="buttonDisabled"
                    :class="isSearchActive ? 'search-active' : ''" 
                    @click="clearSearch"
                >
                    <!-- <b-icon icon="x" class=""></b-icon> -->
                    <span class="font-size-80 text-uppercase">{{ buttonText }}</span>
                </b-button>
            </b-input-group-append>
        </b-input-group>
    </div>
</template>

<script>
import uniqueId from 'lodash/uniqueId';

export default {
    name: 'BaseSearchBox',
    components: {},
    i18n: {
        messages: {
            en: {
                baseSearchBox: {
                    searchPlaceholder: 'Search...',
                    clear: 'Clear',
                    reset: 'Reset',
                },
            },
            es: {
                baseSearchBox: {
                    searchPlaceholder: 'Buscar...',
                    clear: 'Limpiar',
                    reset: 'Restablecer',
                },
            },
            pt: {
                baseSearchBox: {
                    searchPlaceholder: 'Buscar...',
                    clear: 'Limpar',
                    reset: 'Redefinir',
                },
            },
            'zh-Hans': {
                baseSearchBox: {
                    searchPlaceholder: '搜索...',
                    clear: '清除',
                    reset: '重置',
                },
            },
            'zh-Hant': {
                baseSearchBox: {
                    searchPlaceholder: '搜尋...',
                    clear: '清除',
                    reset: '重設',
                },
            },
        },
    },
    props: {
        value: {  // the v-model -- need this so you can easily reset value from parent without triggering new search event
            type: String,
        },
        inline: {  // show as inline?
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            default: function() {
                return uniqueId();
            }
        },
        lazy: { // update on blur (true), or on all input events (false)?
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',  // when empty, the translated default 'Search...' is used (see finalPlaceholder)
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            localValue: '',  // the LOCAL value... hold here until we want to trigger search, then emit this
            isSearchActive: false,  // set true when you emit 'search' -- if you 'CLEAR' and you have not emitted, then don't bother emitting 'reset'
            // you could also show a UI difference when isSearchActive is true (because search is active)
        }
    },
    computed: {
        finalPlaceholder() {
            // the placeholder prop (parent override) or the translated default
            return this.placeholder || this.$t('baseSearchBox.searchPlaceholder');
        },
        buttonDisabled() {
            // should the clear/reset button be disabled?
            // if value is empty AND search is not active, then we can disable it
            return !this.isSearchActive && !this.localValue;
        },
        buttonText() {
            // should we show "CLEAR" or "RESET"?
            // show "CLEAR" when we have a localValue, but no search active
            // show "RESET" when we have search active at all
            // if neither (button in disabled state) then you can show CLEAR, but it will be disabled
            return this.isSearchActive ? this.$t('baseSearchBox.reset') : this.$t('baseSearchBox.clear');
        },
    },
    watch: {
        localValue: function(newVal, oldVal) {
            // watch localValue (the search string)
            // if it was not empty, but now empty, user manually deleted the query
            // OR, it was cleared using the 'X' shown in type="search" inputs (which we must use here for it to work right on mobile)
            // trigger clearSearch() in that case?
            if (!!oldVal && !newVal) {
                this.clearSearch();  // we want this, because it has logic for knowing if search was already active
            }
        },
    },
    methods: {
        handleError: function(error, message) {  // not needed...
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        clearSearch() {
            this.$nextTick(() => {  // inside nextTick so v-model can update
                // NO LONGER NEEDED this.triggerSearch();  // then call your normal triggerSearch thing
                // and new reset stuff
                const shouldEmitReset = this.isSearchActive;  // need to check before calling reset() - we reset isSearchActive in there
                this.reset();
                if (shouldEmitReset) {
                    this.emitReset();  // can replace other stuff above with this once you go to events instead of v-model
                }
            });
            // just reset value and emit normal search event -- you just want to execute search with blank value here
        },
        /* triggerSearch: function() {  // when hitting 'enter' in the search box, or from clearSearch
            // supporting both new and old ways for now... Aug 17 2022
            this.$emit('trigger-search');
            this.emitSearch();
        }, */
        // NEW METHODS - Aug 16 2022 - you just had v-model in parent with the value here... but then search string would get used even if user never hit 'enter' - caused errors
        reset() {
            // reset local things - follow with emitReset() if called from here, but not if called from parent
            // CALLED FROM PARENT - when parent wants to make sure we are cleared
            // do not emit 'reset' event back to parent... that's only when things are triggered from here?
            this.localValue = '';
            this.isSearchActive = false;
        },
        emitSearch() {
            // emit event to parent saying 'search with this string' - using localValue
            // we also call this on ENTER and TAB keys - but if new localValue is empty, then we will RESET things instead
            if (!this.localValue) {
                // did we change local value to ''?  then reset instead
                this.reset();
                this.emitReset();
            } else {
                this.isSearchActive = true;
                this.$emit('search', this.localValue);
            }
        },
        emitReset() {
            // tell parent to reset, clear its stored value, and likely parent will want to do a new search
            // parent can also tell US to reset -- don't emit this when that happens???
            this.$emit('reset');
        }
    }
}
</script>

<style lang="scss" scoped>

.spl-inputs-search {
    #search-clear input[type=search] {
        display: none;
        // this is the built in X to clear the search - we'll hide it
        // but we still need type="search" to make mobile browsers handle it right
        display: none;
    }
    background-color: #fff;
    input,
    .input-group-text {
        border-radius: 0;
        border-color: transparent;
        box-shadow: none;
        background-color: transparent;
        transition: 0.2s all ease-out;
    }
    .input-group-text {
        font-size: 0.8rem;
    }
    button {
        background-color: var(--primary-100);
        border-color: transparent;
        color: var(--primary-700);
        box-shadow: none;
        &:hover,
        &.hover {
            box-shadow: none;
            background-color: rgba(0,0,0,0.05);
        }
        &.search-active {
            color: #fff;
            background-color: var(--primary-500);
            &:hover, 
            &.hover {
                background-color: var(--primary-600);
            }
        }
    }
    button.disabled {
        background-color: transparent;
        color: var(--primary-200);
        box-shadow: none;
        &:hover,
        &.hover {
            box-shadow: none;
        }
    }

    &:focus,
    &.focus,
    &.filled {
        box-shadow: 0 0 0.5rem var(--primary-200);
        background-color: #fff;
    }
}

</style>